import * as Sentry from '@sentry/react';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { getErrorStatus, getErrorMessage } from '@common/utils';
import { toast } from 'react-toastify';
import Lang from 'lang.js';
import langMessages from 'src/lang/langMessages';
import { RouterProvider } from 'react-router-dom';
import { useEffect } from 'react';
import { StatusCodes } from 'http-status-codes';
import { EnvironmentTypes } from '@common/constants';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { AbilityProvider } from './context/AbilityContext';
import RouterWrapper from './router';
import { isFilterNotFoundError } from './utils/filterNotFound';

// TELV1-544 — global query-error backstop. The dashboard had NO user-visible
// error surface for data fetches: axiosInstanceAnalyze only reports to
// Sentry, and the 404 interceptor covers only /organization|/workspace URLs.
// Every failed query rendered as an innocent empty state ("No results",
// blank report) — repeatedly reported as "my data disappeared". This toast
// is the floor; the worst views additionally render their own error states.
//
// Dedupe per queryHash so a poll/refetch loop can't stack toasts; a later
// success clears the entry so a NEW failure of the same query surfaces
// again. 401 is excluded — the axios interceptor owns session expiry.
const toastedQueryErrors = new Set<string>();

// Outside the React tree, so resolve the locale the same way LangProvider
// seeds it (user preference is persisted to localStorage on change).
const globalErrorMessage = (): string => {
  const lang = new Lang({ messages: langMessages });
  lang.setLocale(localStorage.getItem('selectedLanguage') || 'en');
  return lang.get('msg.somethingWentWrong');
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      const errorStatus = getErrorStatus(error);
      if (errorStatus === StatusCodes.UNAUTHORIZED) return;
      // TELV1-606 — a stale filter_id is recovered page-wide by
      // useStaleFilterRecovery (drops it, shows its own message); a generic
      // "Something went wrong" per failed query would only add noise.
      if (isFilterNotFoundError(error)) return;
      if (toastedQueryErrors.has(query.queryHash)) return;
      // Bound the dedupe set: paginated/filtered queries produce a fresh
      // hash per variant, so a long never-recovering session could grow it
      // unbounded (PR review). Resetting rarely re-toasts at worst.
      if (toastedQueryErrors.size > 500) toastedQueryErrors.clear();
      toastedQueryErrors.add(query.queryHash);
      toast.error(globalErrorMessage(), { toastId: `query-error-${query.queryHash}` });
    },
    onSuccess: (_data, query) => {
      toastedQueryErrors.delete(query.queryHash);
    },
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        const errorStatus = getErrorStatus(error);
        if (errorStatus >= 400 && errorStatus < 500) {
          return false;
        }
        return failureCount < 3;
      },
    },
  },
});

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: import.meta.env.MODE !== EnvironmentTypes.DEVELOPMENT,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || EnvironmentTypes.PRODUCTION,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  tracePropagationTargets: ['*'],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.

  // Better error grouping for similar issues
  attachStacktrace: true,

  // Allow for custom fingerprinting rules
  beforeSend(event, hint) {
    // Add release information when available in env
    if (import.meta.env.VITE_APP_VERSION) {
      // eslint-disable-next-line no-param-reassign -- Sentry beforeSend mutates its event by contract
      event.release = import.meta.env.VITE_APP_VERSION;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Sentry hint is untyped
    const error = hint?.originalException as any;
    const status = getErrorStatus(error);
    const message = getErrorMessage(error);

    // Filter out common non-critical HTTP errors
    const filteredStatuses = [
      StatusCodes.BAD_REQUEST,
      StatusCodes.UNAUTHORIZED,
      StatusCodes.FORBIDDEN,
      StatusCodes.NOT_FOUND,
    ];

    if (filteredStatuses.includes(status)) {
      return null;
    }

    // TODO: Remove this filter after fixing the root cause
    // Related ticket should address:
    // 1. Uncompressed Asset errors - likely build/CDN compression configuration
    // Filter out asset loading errors (temporary fix to reduce Sentry noise)
    const assetErrorPatterns = ['Uncompressed Asset'];

    if (
      assetErrorPatterns.some((pattern) => message.toLowerCase().includes(pattern.toLowerCase()))
    ) {
      return null;
    }

    return event;
  },

  // Custom error tags
  initialScope: {
    tags: {
      app: 'dashboard',
      environment: import.meta.env.MODE || EnvironmentTypes.PRODUCTION,
    },
  },
});

// Initialize PostHog for analytics and session recording
// Only enabled when API key is configured and not in development
if (
  import.meta.env.VITE_PUBLIC_POSTHOG_KEY &&
  import.meta.env.MODE !== EnvironmentTypes.DEVELOPMENT
) {
  try {
    posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
      api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
      person_profiles: 'identified_only',
      // Only enable session recording in production (not staging)
      disable_session_recording: import.meta.env.MODE !== EnvironmentTypes.PRODUCTION,
      loaded: (posthogInstance) => {
        // Register super properties that will be sent with all events
        posthogInstance.register({
          app: 'dashboard',
          environment: import.meta.env.MODE || EnvironmentTypes.PRODUCTION,
          version: import.meta.env.VITE_APP_VERSION,
        });
      },
    });
  } catch {
    // Analytics failure should not break the application
  }
}

function Root() {
  useEffect(() => {
    const getClientIP = async () => {
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        const { ip } = await response.json();
        localStorage.setItem('client_ip', ip);
      } catch (error) {
        console.error('Failed to get client IP:', error);
      }
    };

    getClientIP();
  }, []);

  return (
    <PostHogProvider client={posthog}>
      <QueryClientProvider client={queryClient}>
        <AbilityProvider>
          <RouterProvider router={RouterWrapper()} />
          <ReactQueryDevtools initialIsOpen={false} />
        </AbilityProvider>
      </QueryClientProvider>
    </PostHogProvider>
  );
}

export default Root;
